import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, Repeat } from "lucide-react";

const CollectionsSection = () => {
  const navigate = useNavigate();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isLoopEnabled, setIsLoopEnabled] = useState(true); // Default to true for consistent UX
  const videoRef = useRef(null);

  const collections = [
    {
      title: "Country Collection",
      video: "https://res.cloudinary.com/dbelveonz/video/upload/v1770371960/LAMINANCE_v1joqa.mov",
      features: ["Rustic Finish", "Natural Wood", "Vintage Hardware"],
      category: "country-kitchen"
    },
    {
      title: "Contemporary Collection",
      video: "https://res.cloudinary.com/dbelveonz/video/upload/v1770371813/KITCHEN_REMODEL_final-2.mov_e99rtq.mov",
      features: ["Sleek Lines", "Mixed Materials", "Minimalist Design"],
      category: "contemporary-kitchen"
    },
    {
      title: "Modern Collection",
      video: "https://res.cloudinary.com/dbelveonz/video/upload/v1770372575/WhatsApp_Video_2025-11-12_at_16.10.20_bmjn66.mp4",
      features: ["Smart Storage", "Premium Finish", "European Style"],
      category: "modern-kitchen"
    },
    {
      title: "Urban Collection",
      video: "https://res.cloudinary.com/dbelveonz/video/upload/v1770372466/transition_final_1_a71j3b.mp4",
      features: ["Urban Chic", "Compact Design", "Luxury Finish"],
      category: "urban-kitchen"
    },
    {
      title: "AI Story Collection",
      video: "https://res.cloudinary.com/dbelveonz/video/upload/v1770489838/ai_story_final_thumbnail_rzprxl.mp4",
      features: ["AI-Powered Design", "Smart Integration", "Future Tech"],
      category: "ai-story-kitchen"
    },
    {
      title: "Loop Design Collection",
      video: "https://res.cloudinary.com/dbelveonz/video/upload/v1770489922/loop_vid_2_qibwtr.mp4",
      features: ["Circular Design", "Sustainable Materials", "Flow Optimization"],
      category: "loop-design-kitchen"
    },
  ];

  const handleExploreCollection = (category) => {
    navigate(`/catalog?category=${category}`);
  };

  const handleBookConsultation = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
    navigate('/contact');
  };

  const handleViewAllCollections = () => {
    navigate('/catalog');
  };

  const openVideoModal = (collection) => {
    setSelectedVideo(collection);
    setIsLoopEnabled(true); // Reset loop to true when opening new video
  };

  const closeVideoModal = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setSelectedVideo(null);
  };

  const toggleLoop = () => {
    setIsLoopEnabled(!isLoopEnabled);
    
    // Update the video element's loop attribute immediately
    if (videoRef.current) {
      videoRef.current.loop = !isLoopEnabled;
    }
  };

  // Close modal on ESC key press
  React.useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        closeVideoModal();
      }
    };

    if (selectedVideo) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [selectedVideo]);

  return (
    <section
      className="py-20 bg-gradient-to-br from-white to-gray-50 relative overflow-hidden"
      aria-labelledby="collections-heading"
    >
      {/* Fullscreen Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 bg-black">
          {/* Close Button */}
          <button
            onClick={closeVideoModal}
            className="absolute top-6 right-6 z-50 bg-black/50 hover:bg-black text-white p-3 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white/50"
            aria-label="Close video"
          >
            <X size={32} />
          </button>

          {/* Loop Toggle Button */}
          <button
            onClick={toggleLoop}
            className={`absolute top-6 right-20 z-50 flex items-center gap-2 bg-black/50 hover:bg-black text-white px-4 py-3 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 ${
              isLoopEnabled ? 'text-orange-400' : 'text-white/70'
            }`}
            aria-label={isLoopEnabled ? "Disable loop" : "Enable loop"}
          >
            <Repeat size={24} className={isLoopEnabled ? "text-orange-400" : "text-white/70"} />
            <span className="text-sm font-medium hidden sm:inline">
              {isLoopEnabled ? "Loop: ON" : "Loop: OFF"}
            </span>
          </button>

          {/* Fullscreen Video */}
          <video
            ref={videoRef}
            src={selectedVideo.video}
            className="w-full h-full object-contain"
            autoPlay
            controls
            loop={isLoopEnabled}
            muted
            playsInline
          />
        </div>
      )}

      {/* Background Decorative Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-orange-100 rounded-full -translate-x-1/2 -translate-y-1/2 opacity-60" aria-hidden="true"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-50 rounded-full translate-x-1/3 translate-y-1/3 opacity-70" aria-hidden="true"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <header className="text-center mb-16 px-4">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full mb-4">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" aria-hidden="true"></div>
            <span className="text-sm font-semibold">Premium Collections</span>
          </div>
          
          <h2
            id="collections-heading"
            className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-4"
          >
            Our Exclusive Collections
          </h2>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Discover the perfect blend of style, functionality, and craftsmanship in our carefully curated kitchen collections.
          </p>
          
          {/* Decorative Line */}
          <div className="flex justify-center mt-6" aria-hidden="true">
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"></div>
          </div>
        </header>

        {/* Collections Grid - Updated for 6 items */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 px-4">
          {collections.map((collection, index) => (
            <article
              key={index}
              className="relative overflow-hidden rounded-3xl bg-white shadow-xl hover:shadow-2xl transition-shadow duration-300 group"
              aria-labelledby={`collection-title-${index}`}
            >
              {/* Video Container - Video playing directly */}
              <div 
                className="relative aspect-video overflow-hidden cursor-pointer"
                onClick={() => openVideoModal(collection)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openVideoModal(collection);
                  }
                }}
                aria-label={`Click to view ${collection.title} in fullscreen`}
              >
                {/* Video playing directly in loop */}
                <video
                  src={collection.video}
                  className=" object-cover group-hover:scale-105 transition-transform duration-500"
                  autoPlay
                  loop
                  muted
                  playsInline
                  disablePictureInPicture
                  disableRemotePlayback
                />
                
                {/* Subtle overlay on hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300"></div>
                
                {/* Collection title overlay */}
                
                
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                      <div className="w-0 h-0 border-t-[10px] border-b-[10px] border-l-[16px] border-transparent border-l-orange-600 ml-1"></div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Explore Button */}
              
            </article>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16 px-4">
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-8 text-white max-w-4xl mx-auto shadow-2xl">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Need Custom Design?
            </h3>
            <p className="text-white/90 mb-6 text-lg">
              Our experts can create the perfect kitchen for your space.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleBookConsultation}
                className="bg-white text-orange-600 px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-white/50"
                aria-label="Book Free Consultation"
              >
                Book Free Consultation
              </button>
              <button 
                onClick={handleViewAllCollections}
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold hover:bg-white hover:text-orange-600 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-white/50"
                aria-label="View All Collections"
              >
                View All Collections
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CollectionsSection;












// import React, { useState, useRef } from "react";
// import { useNavigate } from "react-router-dom";
// import { X } from "lucide-react";
// import "./theme.css"; // Import the CSS file

// const CollectionsSection = () => {
//   const navigate = useNavigate();
//   const [selectedVideo, setSelectedVideo] = useState(null);
//   const videoRef = useRef(null);

//   const collections = [
//     {
//       title: "Country Collection",
//       video: "https://res.cloudinary.com/dbelveonz/video/upload/v1770371960/LAMINANCE_v1joqa.mov",
//       features: ["Rustic Finish", "Natural Wood", "Vintage Hardware"],
//       category: "country-kitchen"
//     },
//     {
//       title: "Contemporary Collection",
//       video: "https://res.cloudinary.com/dbelveonz/video/upload/v1770371813/KITCHEN_REMODEL_final-2.mov_e99rtq.mov",
//       features: ["Sleek Lines", "Mixed Materials", "Minimalist Design"],
//       category: "contemporary-kitchen"
//     },
//     {
//       title: "Modern Collection",
//       video: "https://res.cloudinary.com/dbelveonz/video/upload/v1770372575/WhatsApp_Video_2025-11-12_at_16.10.20_bmjn66.mp4",
//       features: ["Smart Storage", "Premium Finish", "European Style"],
//       category: "modern-kitchen"
//     },
//     {
//       title: "Urban Collection",
//       video: "https://res.cloudinary.com/dbelveonz/video/upload/v1770372466/transition_final_1_a71j3b.mp4",
//       features: ["Urban Chic", "Compact Design", "Luxury Finish"],
//       category: "urban-kitchen"
//     },
//   ];

//   const handleExploreCollection = (category) => {
//     navigate(`/catalog?category=${category}`);
//   };

//   const handleBookConsultation = () => {
//     window.scrollTo({
//       top: 0,
//       left: 0,
//       behavior: 'smooth'
//     });
//     navigate('/contact');
//   };

//   const handleViewAllCollections = () => {
//     navigate('/catalog');
//   };

//   const openVideoModal = (collection) => {
//     setSelectedVideo(collection);
//   };

//   const closeVideoModal = () => {
//     if (videoRef.current) {
//       videoRef.current.pause();
//       videoRef.current.currentTime = 0;
//     }
//     setSelectedVideo(null);
//   };

//   // Close modal on ESC key press
//   React.useEffect(() => {
//     const handleEscKey = (event) => {
//       if (event.key === 'Escape') {
//         closeVideoModal();
//       }
//     };

//     if (selectedVideo) {
//       document.addEventListener('keydown', handleEscKey);
//       document.body.style.overflow = 'hidden';
//     }

//     return () => {
//       document.removeEventListener('keydown', handleEscKey);
//       document.body.style.overflow = 'unset';
//     };
//   }, [selectedVideo]);

//   return (
//     <section
//       className="py-20 bg-gradient-to-br from-white to-gray-50 relative overflow-hidden"
//       aria-labelledby="collections-heading"
//       style={{
//         background: "var(--gradient-bg)",
//       }}
//     >
//       {/* Fullscreen Video Modal */}
//       {selectedVideo && (
//         <div 
//           className="fixed inset-0 z-50"
//           style={{
//             backgroundColor: "var(--color-black)",
//           }}
//         >
//           {/* Close Button */}
//           <button
//             onClick={closeVideoModal}
//             className="absolute top-6 right-6 z-50 hover:bg-black text-white p-3 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white/50"
//             aria-label="Close video"
//             style={{
//               backgroundColor: "var(--overlay-semi-dark)",
//             }}
//           >
//             <X size={32} />
//           </button>

//           {/* Fullscreen Video */}
//           <video
//             ref={videoRef}
//             src={selectedVideo.video}
//             className="w-full h-full object-contain"
//             autoPlay
//             controls
//             loop
//             muted
//             playsInline
//           />
//         </div>
//       )}

//       {/* Background Decorative Elements */}
//       <div 
//         className="absolute top-0 left-0 w-72 h-72 rounded-full -translate-x-1/2 -translate-y-1/2 opacity-60" 
//         aria-hidden="true"
//         style={{
//           backgroundColor: "var(--color-primary-100)",
//         }}
//       ></div>
//       <div 
//         className="absolute bottom-0 right-0 w-96 h-96 rounded-full translate-x-1/3 translate-y-1/3 opacity-70" 
//         aria-hidden="true"
//         style={{
//           backgroundColor: "var(--color-amber-50)",
//         }}
//       ></div>
      
//       <div className="max-w-7xl mx-auto relative z-10">
//         {/* Section Header */}
//         <header className="text-center mb-16 px-4">
//           <div 
//             className="inline-flex items-center gap-2 text-orange-700 px-4 py-2 rounded-full mb-4"
//             style={{
//               backgroundColor: "var(--color-primary-100)",
//               color: "var(--color-primary-700)",
//             }}
//           >
//             <div 
//               className="w-2 h-2 rounded-full animate-pulse" 
//               aria-hidden="true"
//               style={{
//                 backgroundColor: "var(--color-primary-500)",
//               }}
//             ></div>
//             <span className="text-sm font-semibold">Premium Collections</span>
//           </div>
          
//           <h2
//             id="collections-heading"
//             className="text-5xl md:text-6xl font-bold bg-clip-text text-transparent mb-4"
//             style={{
//               background: "var(--gradient-primary)",
//               WebkitBackgroundClip: "text",
//               backgroundClip: "text",
//             }}
//           >
//             Our Exclusive Collections
//           </h2>
          
//           <p 
//             className="text-xl leading-relaxed max-w-2xl mx-auto"
//             style={{
//               color: "var(--color-gray-600)",
//             }}
//           >
//             Discover the perfect blend of style, functionality, and craftsmanship in our carefully curated kitchen collections.
//           </p>
          
//           {/* Decorative Line */}
//           <div className="flex justify-center mt-6" aria-hidden="true">
//             <div 
//               className="w-24 h-1 rounded-full"
//               style={{
//                 background: "var(--gradient-primary)",
//               }}
//             ></div>
//           </div>
//         </header>

//         {/* Collections Grid - Only Video and Explore Button */}
//         <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 px-4">
//           {collections.map((collection, index) => (
//             <article
//               key={index}
//               className="relative overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl transition-shadow duration-300"
//               aria-labelledby={`collection-title-${index}`}
//               style={{
//                 backgroundColor: "var(--color-white)",
//                 boxShadow: "var(--shadow-medium)",
//               }}
//             >
//               {/* Video Container - Video playing directly */}
//               <div 
//                 className="relative overflow-hidden cursor-pointer"
//                 onClick={() => openVideoModal(collection)}
//                 role="button"
//                 tabIndex={0}
//                 onKeyDown={(e) => {
//                   if (e.key === 'Enter' || e.key === ' ') {
//                     e.preventDefault();
//                     openVideoModal(collection);
//                   }
//                 }}
//                 aria-label={`Click to view ${collection.title} in fullscreen`}
//               >
//                 {/* Video playing directly in loop */}
//                 <video
//                   src={collection.video}
//                   className="w-full h-full object-cover"
//                   autoPlay
//                   loop
//                   muted
//                   playsInline
//                   disablePictureInPicture
//                   disableRemotePlayback
//                 />
                
//                 {/* Subtle overlay on hover */}
//                 <div 
//                   className="absolute inset-0 transition-all duration-300"
//                   style={{
//                     backgroundColor: "var(--color-transparent)",
//                   }}
//                 ></div>
//               </div>
//             </article>
//           ))}
//         </div>

//         {/* Bottom CTA */}
//         <div className="text-center mt-16 px-4">
//           <div 
//             className="rounded-2xl p-8 text-white max-w-4xl mx-auto shadow-2xl"
//             style={{
//               background: "var(--gradient-primary)",
//               boxShadow: "var(--shadow-large)",
//             }}
//           >
//             <h3 className="text-2xl md:text-3xl font-bold mb-4">
//               Need Custom Design?
//             </h3>
//             <p 
//               className="mb-6 text-lg"
//               style={{
//                 color: "rgba(255, 255, 255, 0.9)",
//               }}
//             >
//               Our experts can create the perfect kitchen for your space.
//             </p>
//             <div className="flex flex-col sm:flex-row gap-4 justify-center">
//               <button 
//                 onClick={handleBookConsultation}
//                 className="px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-white/50"
//                 aria-label="Book Free Consultation"
//                 style={{
//                   backgroundColor: "var(--color-white)",
//                   color: "var(--color-primary-600)",
//                 }}
//               >
//                 Book Free Consultation
//               </button>
//               <button 
//                 onClick={handleViewAllCollections}
//                 className="border-2 text-white px-8 py-4 rounded-xl font-bold hover:bg-white transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-white/50"
//                 aria-label="View All Collections"
//                 style={{
//                   borderColor: "var(--color-white)",
//                   color: "var(--color-white)",
//                 }}
//               >
//                 View All Collections
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default CollectionsSection;